import React, { lazy, useMemo } from 'react'
import OwnerOverview from 'src/Routes/Overview/OwnerOverview'
import DashboardRoute from 'src/Routes/Dashboard/DashboardRoute'
import InvalidPage from 'src/Routes/Error/InvalidPage'
import { InstallationRedirect } from 'src/Libs/installations'
import { Navigate, Route } from 'react-router-dom'
import { INSTALLER_PARTNER, SERVICE_LEVELS } from 'src/Configuration'
import { UserData } from 'src/State/User/UserModel'
import BlankInstallationDashboard from 'src/Routes/Dashboard/blank-view/BlankInstallationDashboard'
import { useTypedSelector } from 'src/State/RootReducer'
import CompanyUserPermissions from 'src/Libs/service-dashboard/components/CompanyUserPermissions'
import { isUndefined } from 'lodash'
import RequireUserLevel from 'src/Elements/RequireUserLevel/RequireUserLevel'
import UnsavedFormsProvider from 'src/Libs/form/provider/ExitUnsavedFormsProvider'
import { InstallationOverviewPage } from '../types'
import { InverterTool } from 'src/Libs/inverter-tool/components'
import RequireStorageId from 'src/Elements/RequireStorageId/RequireStorageId'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import VehicleIntegrationMainPage from 'src/Libs/e-mobility/vehicle-integration/components/VehicleIntegrationMainPage'
import { useShouldEnableEMobilitySection } from 'src/Libs/e-mobility/shared/hooks/use-should-enable-e-mobility-section'
import { AI360MainPage } from 'src/Libs/ai-360/pages'
import { isOwnerOrCompany } from 'src/Libs/installations/utils'
import { isFlowTheme } from 'src/Libs/utils'
import InstallationRouteGuard from '../components/InstallationRouteGuard'

// Routes (App)
const UserSettings = lazy(() => import('src/Routes/User/User'))
const CompanyPage = lazy(() => import('src/Libs/company-settings/pages/CompanyPage'))
const Notifications = lazy(() => import('src/Routes/Notifications/Notifications'))
const MessageOfTheDayOverview = lazy(() => import('src/Routes/MessageOfTheDay/MessageOfTheDayOverview'))
const CreateMessageOfTheDay = lazy(() => import('src/Routes/MessageOfTheDay/CreateMessageOfTheDay'))

// Routes (Service Dashboard)
const ServiceDashboard = lazy(() => import('src/Routes/Dashboard/Service/Dashboard'))
const ServiceUserOverview = lazy(() => import('src/Routes/Dashboard/Service/User'))
const UserInstallations = lazy(() => import('src/Routes/Dashboard/Service/UserInstallationsSystems'))
const CreateUser = lazy(() => import('src/Routes/Dashboard/Service/CreateUser'))
const ServiceCompanyOverview = lazy(() => import('src/Routes/Dashboard/Service/Company'))
const CreateCompany = lazy(() => import('src/Routes/Dashboard/Service/CreateCompany'))
const CompanyUsersHistory = lazy(() => import('src/Libs/service-dashboard/components/CompanyEmployeeHistory'))
const CompanyInstallations = lazy(() => import('src/Libs/service-dashboard/components/CompanyInstallations'))
const InstallationDetails = lazy(() => import('src/Libs/installation-details/components/InstallationDetailsRoute'))

const CommissioningDeclaration = lazy(() => import('src/Routes/CommissioningDeclaration/CommissioningDeclaration'))
const SystemSettingsRoutes = lazy(() => import('src/Routes/SystemSettings/SystemSettingsRoutes'))
const Actuators = lazy(() => import('src/Libs/smart-home/components/Actuators'))

// TODO: installer dashboard is loading every time (even if not used by certain user role --> likely the same for other components)
const InstallerDashboard = lazy(() => import('src/Routes/Dashboard/Installer/Dashboard'))
const EMobilityRoot = lazy(() => import('src/Routes/P3_eMobility/EMobilityRoot'))
const InstallationExtensions = lazy(() => import('src/Routes/installation-extensions/InstallationExtension'))
const Downloads = lazy(() => import('src/Routes/Service/Documents/Downloads'))
const Contact = lazy(() => import('src/Routes/Service/Contact/Contact'))
const ReferalCodesRoute = lazy(() => import('src/Routes/ReferalCodes/ReferalCodes'))
const RetrofitCheck = lazy(() => import('src/Routes/RetrofitCheck/RetrofitCheck'))
const ReferralRoute = lazy(() => import('src/Routes/Referral/ReferralRoute'))
const PvModules = lazy(() => import('src/Routes/Administration/PvModules/PvModules'))
const SmartHome = lazy(() => import('src/Routes/SmartHome/SmartHome'))
const DocumentLinksPage = lazy(() => import('src/Libs/flow/document-links/components/DocumentLinksPage'))

interface UserAppRoutesHook {
  userRoutes: React.ReactNode[]
}

interface Props {
  user: UserData
  installationCount: number
}

/**
 * Hook to provide application wide routes for different user levels
 */
const useAppRoutes = ({ user, installationCount }: Props): UserAppRoutesHook => {
  const installation = useTypedSelector(state => state.storage.installation)
  const userData = useTypedSelector(state => state.user.userData)
  const isOwner = isOwnerOrCompany(installation, userData)
  const {
    featureFlags: { isCustomerReferralEnabled, isExtensionsPageVisibleInFlow },
    isFeatureFlagsLoading,
  } = useFeatureFlagsContext()

  /* Only owner can access extension tab. When reloading page installation or userData can be shortly undefined. To prevent the tab disappearing after reload the undefined checks are added. */
  const allowAccessToExtensionTab = (!isFlowTheme || isExtensionsPageVisibleInFlow) && (isOwner || isUndefined(installation) || isUndefined(userData))

  const { shouldEnableEMobilityRoutes } = useShouldEnableEMobilitySection()

  const userRoutes = useMemo(() => {
    const routes = [
      <Route
        key={'system-settings-routes'}
        path={isFlowTheme ? `/installation/:installationId/:storageId?/properties/*` : `/installation/:installationId/:storageId?/*`}
        element={
          <InstallationRouteGuard>
            <RequireStorageId allowAccessWithoutStorageId>
              <SystemSettingsRoutes />
            </RequireStorageId>
          </InstallationRouteGuard>
        }
      />,
      <Route
        path={`/installation`}
        key={'installation-redirect-by-query'}
        element={
          <RequireUserLevel allowedRoles={SERVICE_LEVELS} userLevel={user.level}>
            <InstallationRedirect />
          </RequireUserLevel>
        }
      />,
      <Route
        path={`/installation/:storageId?/*`}
        key={'installation-redirect'}
        element={
          <RequireUserLevel allowedRoles={SERVICE_LEVELS} userLevel={user.level}>
            <RequireStorageId>
              <InstallationRedirect />
            </RequireStorageId>
          </RequireUserLevel>
        }
      />,
      <Route
        key={'message-of-the-day-overview'}
        path={`/message-of-the-day/:id?`}
        element={
          <RequireUserLevel allowedRoles={SERVICE_LEVELS} userLevel={user.level}>
            <UnsavedFormsProvider>
              <MessageOfTheDayOverview />
            </UnsavedFormsProvider>
          </RequireUserLevel>
        }
      />,
      <Route
        key={'create-message-of-the-day'}
        path={`/create-message-of-the-day`}
        element={
          <RequireUserLevel allowedRoles={SERVICE_LEVELS} userLevel={user.level}>
            <UnsavedFormsProvider>
              <CreateMessageOfTheDay />
            </UnsavedFormsProvider>
          </RequireUserLevel>
        }
      />,
      <Route
        key={'actuators'}
        path={`/actuators/:storageId?`}
        element={
          <RequireStorageId>
            <Actuators />
          </RequireStorageId>
        }
      />,
      <Route
        key={'commissioning-declaration'}
        path={`/commissioning-declaration/:storageId?`}
        element={
          <RequireStorageId>
            <CommissioningDeclaration />
          </RequireStorageId>
        }
      />,
      <Route
        key={'pv-modules'}
        path={`/pv-modules/:storageId?`}
        element={
          <RequireStorageId>
            <PvModules />
          </RequireStorageId>
        }
      />,
      <Route
        key={'vehicle-integration'}
        path={`/vehicle-integration/:installationId/:storageId?/vehicle-integration/*`}
        element={
          <InstallationRouteGuard>
            <RequireStorageId allowAccessWithoutStorageId>
              <VehicleIntegrationMainPage />
            </RequireStorageId>
          </InstallationRouteGuard>
        }
      />,
      <Route
        key={'ai-360'}
        path={`/dashboard/ai360/:installationId/:storageId?/*`}
        element={
          <InstallationRouteGuard>
            <RequireStorageId>
              <AI360MainPage />
            </RequireStorageId>
          </InstallationRouteGuard>
        }
      />,
      <Route
        key={'installation-extensions'}
        path={`/installation-extensions/:installationId/:storageId?`}
        element={
          <InstallationRouteGuard>
            <RequireUserLevel allowedRoles={allowAccessToExtensionTab ? ['USER'] : []} userLevel={user.level} loading={isFeatureFlagsLoading}>
              <RequireStorageId>
                <InstallationExtensions />
              </RequireStorageId>
            </RequireUserLevel>
          </InstallationRouteGuard>
        }
      />,
      <Route
        key={'downloads'}
        path={`/downloads/:categoryId?`}
        element={
          <RequireUserLevel allowedRoles={[...SERVICE_LEVELS, ...INSTALLER_PARTNER, 'USER']} userLevel={user.level}>
            <Downloads />
          </RequireUserLevel>
        }
      />,
      <Route
        key={'smart-home'}
        path={`/smart-home/:installationId/:storageId?`}
        element={
          <RequireUserLevel allowedRoles={[...SERVICE_LEVELS, ...INSTALLER_PARTNER, 'USER']} userLevel={user.level} loading={isFeatureFlagsLoading}>
            <InstallationRouteGuard>
              <RequireStorageId>
                <SmartHome />
              </RequireStorageId>
            </InstallationRouteGuard>
          </RequireUserLevel>
        }
      />,
      <Route key={'contact'} path={`/contact`} element={<Contact />} />,
      <Route
        key={'referal-codes'}
        path={`/referal-codes`}
        element={
          <RequireUserLevel allowedRoles={SERVICE_LEVELS} userLevel={user.level}>
            <ReferalCodesRoute />
          </RequireUserLevel>
        }
      />,
      <Route key={'document-links'} path={`/document-links`} element={<DocumentLinksPage />} />,
      <Route
        key={'referral'}
        path={`/referral`}
        element={
          <RequireUserLevel allowedRoles={isCustomerReferralEnabled ? ['USER'] : []} userLevel={user.level} loading={isFeatureFlagsLoading}>
            <ReferralRoute />
          </RequireUserLevel>
        }
      />,
      <Route
        key={'inverter-tool'}
        path={'/inverter-tool'}
        element={
          <RequireUserLevel allowedRoles={[...SERVICE_LEVELS, ...INSTALLER_PARTNER]} userLevel={user.level}>
            <InverterTool />
          </RequireUserLevel>
        }
      />,
      <Route key={'user'} path={`/user`} element={<UserSettings />} />,
      <Route key={'company'} path={`/company/*`} element={<CompanyPage />} />,
      <Route key={'notifications'} path={`/notifications/:notificationId?`} element={<Notifications />} />,
      <Route key={'not-found'} path="/not-found" element={<InvalidPage />} />,
      <Route
        key={'retrofit-check'}
        path={`/retrofit-check/:installationId/:storageId?`}
        element={
          <InstallationRouteGuard>
            <RequireStorageId>
              <RetrofitCheck />
            </RequireStorageId>
          </InstallationRouteGuard>
        }
      />,
    ]

    if (user.level === 'USER' && installationCount === 0) {
      routes.push(<Route key={'blank-installation-dashboard'} path="/dashboard/*" element={<BlankInstallationDashboard />} />)
    } else {
      routes.push(<Route key={'dashboard-route'} path={'/dashboard/*'} element={<DashboardRoute />} />)
    }

    if (user.level === 'USER' && installationCount > 1) {
      routes.push(<Route key={'owner-overview'} path={InstallationOverviewPage.Overview} element={<OwnerOverview />} />)
    } else {
      routes.push(
        <Route
          key={'installer-dashboard'}
          path="/installer-dashboard/*"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={INSTALLER_PARTNER}>
              <InstallerDashboard />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'service-user-installations'}
          path="/overview/users/:id/installations"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <UserInstallations />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'service-user-overview'}
          path="/overview/users/:id?"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <ServiceUserOverview />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'create-user'}
          path="/create-user"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <CreateUser />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'company-user-permissions'}
          path="/overview/companies/:id/users"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <CompanyUserPermissions />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'company-users-history'}
          path="/overview/companies/:id/history"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <CompanyUsersHistory />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'company-installations'}
          path="/overview/companies/:id/installations"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <CompanyInstallations />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'service-company-overview'}
          path="/overview/companies/:id?"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <ServiceCompanyOverview />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'create-company'}
          path="/create-company"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <CreateCompany />
            </RequireUserLevel>
          }
        />,
        <Route
          key={'installation-details'}
          path="/overview/installations/:installationId/:storageId?/*"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <InstallationRouteGuard>
                <RequireStorageId>
                  <InstallationDetails />
                </RequireStorageId>
              </InstallationRouteGuard>
            </RequireUserLevel>
          }
        />,
        <Route
          key={'service-dashboard'}
          path="/overview/installations/:installationId?"
          element={
            <RequireUserLevel userLevel={user.level} allowedRoles={SERVICE_LEVELS}>
              <ServiceDashboard />
            </RequireUserLevel>
          }
        />,
        <Route key={'redirect-to-root'} path={InstallationOverviewPage.Overview} element={<Navigate to="/" />} />
      )
    }

    if (shouldEnableEMobilityRoutes) {
      routes.push(
        <Route
          key={'e-mobility'}
          path={`/e-mobility/:installationId/:storageId?/*`}
          element={
            <RequireStorageId allowAccessWithoutStorageId>
              <InstallationRouteGuard>
                <EMobilityRoot installationId={installation?.id} />
              </InstallationRouteGuard>
            </RequireStorageId>
          }
        />
      )
    }

    return routes
  }, [
    user,
    allowAccessToExtensionTab,
    isFeatureFlagsLoading,
    isCustomerReferralEnabled,
    installationCount,
    shouldEnableEMobilityRoutes,
    installation?.id,
  ])

  return { userRoutes }
}

export default useAppRoutes
